import type { UnitInfoGrizzlyScreenSGInterface } from 'store/units/types';
import { BaseUnitConfig } from 'unitData/BaseUnitConfig';
import type { UnitConfigParams } from 'unitData/BaseUnitConfig';
import type {
  GrizzlyScreenUnitInfoDataInterface,
  ScreeningMediaIdInterface,
} from './unitInfoData';

export const nbrOfDecksOptions = [1, 2];
export const defaultDeck2ScreeningMediaValue = 'WX1000_WIRE_MESH';

export const getDefaultDeck2ScreeningElement = (
  unitInfoData: GrizzlyScreenUnitInfoDataInterface,
): ScreeningMediaIdInterface =>
  unitInfoData.configurations.normal.screeningMediaIds.find(
    (media) => media.value === defaultDeck2ScreeningMediaValue,
  ) ??
  unitInfoData.configurations.normal.screeningMediaIds[0] ?? {
    name: 'WX1000 (Wire mesh)',
    value: defaultDeck2ScreeningMediaValue,
  };

export const filterModels = (
  models: string[],
  nbrOfDecks: number,
): string[] => {
  if (nbrOfDecks === 1) {
    return models.filter(
      (m) => m.charAt(m.length - 1) === '1' || m.charAt(m.length - 2) === '1',
    );
  } else {
    return models.filter(
      (m) => m.charAt(m.length - 1) === '2' || m.charAt(m.length - 2) === '2',
    );
  }
};

export const getDefaultData = (
  unitInfoData: GrizzlyScreenUnitInfoDataInterface,
): UnitInfoGrizzlyScreenSGInterface => {
  const defaultModel = Object.entries(unitInfoData.models)[0];
  const model = defaultModel[0];
  const modelData = defaultModel[1];

  const grizzlyUnitInfo: UnitInfoGrizzlyScreenSGInterface = {
    modelName: model,
    nbrOfDecks: 1,
    length: modelData.length,
    width: modelData.width,
    maxFeedSize: modelData.maxFeedSize,
    motor: modelData.motor,
    vibUnit: modelData.vibUnit,
    separationRange: modelData.separationRange,
    separationRangeDeck2: modelData.separationRangeDeck2,
    deckList: {
      0: {
        separationSize: 100,
        screeningElement: getDefaultDeck2ScreeningElement(unitInfoData),
        efficiencyScreeningElement:
          unitInfoData.configurations.efficiency.screeningMediaIds[0],
        maxAmountOfUnderSize:
          unitInfoData.configurations.normal.overUnderSize[1],
        openArea: 0,
        particleShape: unitInfoData.configurations.efficiency.particleShapes[0],
        efficiency:
          unitInfoData.configurations.efficiency.efficiencyParameters[0],
        efficiencyNumber: 0,
        display: {
          separationSize: true,
          screeningElement: false,
        },
      },
    },

    parallelUnits: 1,
    moisture: 0,
    newMoisture: false,
    materialType: unitInfoData.configurations.rockMaterials[0],
    display: {
      displayLoad: true,
      displayModelName: true,
      displayUnitName: true,
      displayNumberOfDecks: false,
      displayNotes: {
        noteOne: false,
        noteTwo: false,
        noteThree: false,
        noteFour: false,
        noteFive: false,
      },
    },
  };
  return grizzlyUnitInfo;
};

export const getDefaultSeparationDeck = (
  unitInfoData: GrizzlyScreenUnitInfoDataInterface,
) => {
  return {
    separationSize: 30,
    screeningElement: getDefaultDeck2ScreeningElement(unitInfoData),
    efficiencyScreeningElement:
      unitInfoData.configurations.efficiency.screeningMediaIds[0],
    maxAmountOfUnderSize: unitInfoData.configurations.normal.overUnderSize[1],
    openArea: 0,
    particleShape: unitInfoData.configurations.efficiency.particleShapes[0],
    efficiency: unitInfoData.configurations.efficiency.efficiencyParameters[0],
    efficiencyNumber: 0,
    display: {
      separationSize: true,
      screeningElement: true,
    },
  };
};

export class GrizzlyScreenConfig extends BaseUnitConfig {
  static getDefaultData(
    params?: UnitConfigParams,
  ): UnitInfoGrizzlyScreenSGInterface {
    const unitInfoData =
      params?.data as unknown as GrizzlyScreenUnitInfoDataInterface;

    const defaultModel = Object.entries(unitInfoData.models)[0];
    const model = defaultModel[0];
    const modelData = defaultModel[1];

    return {
      modelName: model,
      nbrOfDecks: 1,
      length: modelData.length,
      width: modelData.width,
      maxFeedSize: modelData.maxFeedSize,
      motor: modelData.motor,
      vibUnit: modelData.vibUnit,
      separationRange: modelData.separationRange,
      separationRangeDeck2: modelData.separationRangeDeck2,
      deckList: {
        0: {
          separationSize: 100,
          screeningElement: getDefaultDeck2ScreeningElement(unitInfoData),
          efficiencyScreeningElement:
            unitInfoData.configurations.efficiency.screeningMediaIds[0],
          maxAmountOfUnderSize:
            unitInfoData.configurations.normal.overUnderSize[1],
          openArea: 0,
          particleShape:
            unitInfoData.configurations.efficiency.particleShapes[0],
          efficiency:
            unitInfoData.configurations.efficiency.efficiencyParameters[0],
          efficiencyNumber: 0,
          display: {
            separationSize: true,
            screeningElement: false,
          },
        },
      },
      ...BaseUnitConfig.getBaseDefaults(),
      materialType: unitInfoData.configurations.rockMaterials[0],
      display: {
        ...BaseUnitConfig.getDefaultDisplay(),
        displayLoad: true,
        displayRiskLevel: false,
        displayNumberOfDecks: false,
      },
    };
  }

  static getNumberOfPorts(): number {
    return 3;
  }
}

export const grizzlyScreenConfig = new GrizzlyScreenConfig();
